/**
 * ELITE BATCH 24 PART 8 — Data/AI Agents + Productivity Workflows (71-80)
 *
 * PRODUCTION ENHANCEMENTS:
 * 1. Case stamping — node 2 stamps case_id, timestamps, workflow version
 * 2. Confidence-gated AI — aiNodeV2 always outputs confidence_score + evidence
 * 3. Confidence routing — confidenceGate routes auto vs human review
 * 4. Post-execution verification — verifyNode confirms actions succeeded
 * 5. Correct field names: conditionData, approvalGateData, delayAction, code
 *
 * Each workflow: 10 nodes, proper edges, detailed summary.
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED V2 HELPERS ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 24 PART 8 — Data/AI Agents + Productivity Workflows (71-80)
// =============================================================================

export const ELITE_BATCH24_PART8: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 71. Elite: Company Research Agent
  // ===================================================================
  "Elite: Company Research Agent": {
    nodes: [
      trigger("Slack: Research request submitted", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when a team member posts a company research request in a designated Slack channel (e.g., #research-requests). The message should contain the company name and any specific research angles needed.",
        setup:
          "Create a #research-requests Slack channel. Configure a Slack bot to forward messages from this channel to the webhook. Ensure Clearbit and LinkedIn API keys are configured for enrichment.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Research agent case",
        200,
        "Company Research Agent",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Deep research company from web data",
        360,
        "You are a corporate intelligence analyst. Synthesize company data from Clearbit firmographics and LinkedIn presence into a comprehensive research dossier. Cover company overview, market position, recent news/funding, key executives, tech stack, competitive landscape, and potential partnership or sales opportunities. Output company_summary (3-paragraph overview), key_executives (comma-separated names and titles), funding_stage, estimated_revenue_range, tech_stack (comma-separated), competitive_position, and opportunity_assessment (1-2 sentences on sales/partnership potential).",
        "Research request: {{{1.text}}}. Clearbit data: domain {{{4.domain}}}, name {{{4.company_name}}}, industry {{{4.industry}}}, employee_count {{{4.employees}}}, funding {{{4.funding_total}}}, tech_tags {{{4.tech_tags}}}, description {{{4.description}}}. LinkedIn data: follower_count {{{4.linkedin_followers}}}. Build a comprehensive dossier.",
        [
          { field: "company_summary", as: "company_summary", type: "string" },
          { field: "key_executives", as: "key_executives", type: "string" },
          { field: "funding_stage", as: "funding_stage", type: "string" },
          {
            field: "estimated_revenue_range",
            as: "revenue_range",
            type: "string",
          },
          { field: "tech_stack", as: "tech_stack", type: "string" },
          {
            field: "competitive_position",
            as: "competitive_position",
            type: "string",
          },
          {
            field: "opportunity_assessment",
            as: "opportunity_assessment",
            type: "string",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Clearbit + LinkedIn: Fetch company firmographics",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://company.clearbit.com/v2/companies/find?domain={{{1.company_domain}}}",
            headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
            body: [],
            variables: [
              { name: "domain", customName: "domain", type: "string" },
              { name: "name", customName: "company_name", type: "string" },
              {
                name: "category.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "metrics.employees",
                customName: "employees",
                type: "number",
              },
              {
                name: "metrics.raised",
                customName: "funding_total",
                type: "string",
              },
              { name: "tech", customName: "tech_tags", type: "string" },
              {
                name: "description",
                customName: "description",
                type: "string",
              },
              {
                name: "linkedin.handle",
                customName: "linkedin_followers",
                type: "string",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI research confidence?", 680),

      n("6", "hitApi", "Google Docs: Create research dossier", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "Company Research: {{{4.company_name}}} — {{{2.case_id}}}",
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "revisionId", customName: "doc_revision", type: "string" },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Analyst reviews dossier quality",
        840,
        "Company research dossier for {{{4.company_name}}} ({{{4.industry}}}). {{{4.employees}}} employees, funding: {{{3.funding_stage}}}. Revenue range: {{{3.revenue_range}}}. Opportunity: {{{3.opportunity_assessment}}}. Confidence: {{{3.ai_confidence}}}%. Approve to publish dossier.",
        24,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Google Doc created",
        1000,
        "GET",
        "https://docs.googleapis.com/v1/documents/{{{6.doc_id}}}",
        [{ key: "Authorization", value: "Bearer {google_token}" }],
        [
          { name: "documentId", customName: "verified_doc_id", type: "string" },
          { name: "title", customName: "doc_title", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Doc created?",
        1160,
        "verified_doc_id",
        "IS NOT EMPTY",
        "",
      ),

      sheetsNode(
        "10",
        "Sheets: Log research request",
        1320,
        "CompanyResearch",
        '[["{{{2.case_id}}}","{{{4.company_name}}}","{{{4.industry}}}","{{{4.employees}}}","{{{3.funding_stage}}}","{{{3.revenue_range}}}","{{{3.ai_confidence}}}","{{{6.doc_id}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Receives Slack research requests, enriches via Clearbit firmographics, then uses AI to synthesize a comprehensive company dossier covering market position, executives, tech stack, and sales opportunities. High-confidence dossiers auto-publish to Google Docs; lower-quality research routes to analyst review. Verifies document creation and logs all requests for research operations tracking.",
  },

  // ===================================================================
  // 72. Elite: Meeting Prep Agent
  // ===================================================================
  "Elite: Meeting Prep Agent": {
    nodes: [
      trigger("Calendar: Meeting in 2 hours", "webhook_app", {
        source: "google_calendar",
        event: "event.upcoming",
        howItStarts:
          "Fires 2 hours before a scheduled meeting that has external attendees. Compiles attendee intelligence, account context, and recent interactions to prepare a comprehensive meeting brief for the host.",
        setup:
          "Connect Google Calendar API with push notifications. Configure a 2-hour pre-meeting trigger for events with external domains. Ensure HubSpot and Clearbit API keys are set for attendee enrichment.",
        inputFields: [],
      }),

      caseStampNode("2", "Stamp: Meeting prep case", 200, "Meeting Prep Agent"),

      aiNodeV2(
        "3",
        "OpenAI: Compile attendee and account intelligence",
        360,
        "You are an executive meeting preparation assistant. Compile a concise, actionable meeting brief from attendee profiles, company data, CRM history, and recent interactions. Include attendee backgrounds, their company context, open deals/opportunities, recent support tickets, key talking points, potential objections, and recommended agenda items. Output meeting_brief (structured markdown-format brief), key_talking_points (comma-separated top 5), open_deals_summary, risk_factors (things to watch out for), and recommended_ask (the ideal meeting outcome).",
        "Meeting: {{{1.summary}}}, time {{{1.start.dateTime}}}. Attendees: {{{1.attendees}}}. HubSpot data: contact_company {{{4.company}}}, deal_stage {{{4.deal_stage}}}, deal_amount {{{4.deal_amount}}}, last_activity {{{4.last_activity}}}, lifetime_value {{{4.ltv}}}. Clearbit: industry {{{4.industry}}}, employee_count {{{4.employees}}}. Compile a meeting prep brief.",
        [
          { field: "meeting_brief", as: "meeting_brief", type: "string" },
          { field: "key_talking_points", as: "talking_points", type: "string" },
          { field: "open_deals_summary", as: "deals_summary", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "recommended_ask", as: "recommended_ask", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot + Clearbit: Fetch attendee context", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{{{1.attendee_email}}}?idProperty=email&properties=company,dealstage,amount,hs_last_activity_date,total_revenue,hs_object_id",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.company",
              customName: "company",
              type: "string",
            },
            {
              name: "properties.dealstage",
              customName: "deal_stage",
              type: "string",
            },
            {
              name: "properties.amount",
              customName: "deal_amount",
              type: "string",
            },
            {
              name: "properties.hs_last_activity_date",
              customName: "last_activity",
              type: "string",
            },
            {
              name: "properties.total_revenue",
              customName: "ltv",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI brief confidence?", 680),

      emailNode(
        "6",
        "Email: Send meeting brief to host",
        840,
        "{{{1.organizer.email}}}",
        "Meeting prep: {{{1.summary}}} at {{{1.start.dateTime}}}",
        "{{{3.meeting_brief}}}",
      ),

      approvalNode(
        "7",
        "Approval: Rep edits brief before send",
        840,
        'Meeting brief for "{{{1.summary}}}" with {{{4.company}}}. Deal stage: {{{4.deal_stage}}}, amount: ${{{4.deal_amount}}}. Talking points: {{{3.talking_points}}}. Recommended ask: {{{3.recommended_ask}}}. Confidence: {{{3.ai_confidence}}}%. Approve to send brief to host.',
        2,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Brief delivered",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={{{1.organizer.email}}}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "briefs_sent", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "brief_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Brief delivered?",
        1160,
        "brief_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log meeting prep",
        1320,
        "MeetingPreps",
        '[["{{{2.case_id}}}","{{{1.summary}}}","{{{4.company}}}","{{{4.deal_stage}}}","{{{4.deal_amount}}}","{{{3.recommended_ask}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Triggers 2 hours before meetings with external attendees, enriches attendee profiles via HubSpot and Clearbit, then uses AI to compile a comprehensive brief with talking points, deal context, risk factors, and a recommended ask. High-confidence briefs auto-send; uncertain ones route to the rep for editing. Verifies delivery and logs all preps for meeting effectiveness analytics.",
  },

  // ===================================================================
  // 73. Elite: Call Coaching Analyzer
  // ===================================================================
  "Elite: Call Coaching Analyzer": {
    nodes: [
      trigger("Gong: Call recording completed", "webhook_app", {
        source: "gong",
        event: "call.completed",
        howItStarts:
          "Fires when a sales call recording finishes processing in Gong. The workflow analyzes the call transcript for coaching opportunities including talk ratio, objection handling, discovery quality, and next-step setting.",
        setup:
          "In Gong > Settings > Integrations, configure webhook for call.completed events. Ensure transcription is enabled and the API key has read access to call transcripts and metadata.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Call coaching case",
        200,
        "Call Coaching Analyzer",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze talk ratio, objections, and sentiment",
        360,
        "You are a sales coaching expert. Analyze the call transcript to provide actionable coaching feedback. Evaluate talk-to-listen ratio (ideal <40% rep talk), discovery question quality, objection handling effectiveness, next-step commitment, and overall call sentiment. Output talk_ratio_pct (rep talk percentage), discovery_score (1-10), objection_handling_score (1-10), next_steps_set (yes/no), overall_score (1-100), coaching_note (2-3 specific actionable coaching tips), top_objection (the main objection raised), and call_sentiment (positive/neutral/negative).",
        "Call transcript from Gong: {{{4.transcript}}}. Call metadata: duration {{{4.duration_minutes}}} min, rep {{{4.rep_name}}}, prospect {{{4.prospect_name}}}, deal_stage {{{4.deal_stage}}}. Analyze for coaching opportunities.",
        [
          { field: "talk_ratio_pct", as: "talk_ratio", type: "number" },
          { field: "discovery_score", as: "discovery_score", type: "number" },
          {
            field: "objection_handling_score",
            as: "objection_score",
            type: "number",
          },
          { field: "next_steps_set", as: "next_steps_set", type: "string" },
          { field: "overall_score", as: "overall_score", type: "number" },
          { field: "coaching_note", as: "coaching_note", type: "string" },
          { field: "top_objection", as: "top_objection", type: "string" },
          { field: "call_sentiment", as: "call_sentiment", type: "string" },
        ],
      ),

      n("4", "hitApi", "Gong: Fetch call transcript and metadata", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.gong.io/v2/calls/transcript",
          headers: [
            { key: "Authorization", value: "Basic {gong_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "filter", value: '{"callIds":["{{{1.call_id}}}"]}' }],
          variables: [
            {
              name: "callTranscripts[0].transcript",
              customName: "transcript",
              type: "string",
            },
            {
              name: "callTranscripts[0].duration",
              customName: "duration_minutes",
              type: "number",
            },
            {
              name: "callTranscripts[0].parties[0].name",
              customName: "rep_name",
              type: "string",
            },
            {
              name: "callTranscripts[0].parties[1].name",
              customName: "prospect_name",
              type: "string",
            },
            {
              name: "callTranscripts[0].dealStage",
              customName: "deal_stage",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI coaching confidence?", 680),

      slackNode(
        "6",
        "Slack: Send coaching note to rep",
        840,
        "Call Coaching for {{{4.rep_name}}} | {{{4.prospect_name}}} ({{{4.duration_minutes}}} min)\n\nOverall Score: {{{3.overall_score}}}/100\nTalk Ratio: {{{3.talk_ratio}}}% (target <40%)\nDiscovery: {{{3.discovery_score}}}/10 | Objections: {{{3.objection_score}}}/10\nNext Steps Set: {{{3.next_steps_set}}} | Sentiment: {{{3.call_sentiment}}}\nTop Objection: {{{3.top_objection}}}\n\nCoaching Tips:\n{{{3.coaching_note}}}\n\nCase: {{{2.case_id}}}",
      ),

      approvalNode(
        "7",
        "Approval: Sales manager reviews coaching",
        840,
        "Call coaching for {{{4.rep_name}}}: overall {{{3.overall_score}}}/100, talk ratio {{{3.talk_ratio}}}%, discovery {{{3.discovery_score}}}/10. Coaching: {{{3.coaching_note}}}. Confidence: {{{3.ai_confidence}}}%. Approve to share coaching feedback with rep.",
        12,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Coaching delivered via Slack",
        1000,
        "GET",
        "https://slack.com/api/conversations.history?channel={slack_coaching_channel}&limit=1",
        [{ key: "Authorization", value: "Bearer {slack_bot_token}" }],
        [
          {
            name: "messages[0].ts",
            customName: "slack_message_ts",
            type: "string",
          },
          { name: "ok", customName: "slack_delivered", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Coaching delivered?",
        1160,
        "slack_delivered",
        "EQUALS",
        "true",
      ),

      sheetsNode(
        "10",
        "Sheets: Log call coaching",
        1320,
        "CallCoaching",
        '[["{{{2.case_id}}}","{{{4.rep_name}}}","{{{4.prospect_name}}}","{{{3.overall_score}}}","{{{3.talk_ratio}}}","{{{3.discovery_score}}}","{{{3.objection_score}}}","{{{3.call_sentiment}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Analyzes completed Gong call recordings by fetching transcripts and using AI to evaluate talk ratio, discovery quality, objection handling, and next-step commitment. Generates actionable coaching notes with specific improvement tips. High-confidence coaching auto-sends via Slack; sensitive feedback routes to sales manager approval. Verifies delivery and logs all coaching for rep development tracking.",
  },

  // ===================================================================
  // 74. Elite: Support Copilot
  // ===================================================================
  "Elite: Support Copilot": {
    nodes: [
      trigger("Zendesk: New support ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created in Zendesk. The copilot classifies the issue, searches the knowledge base for relevant articles, and drafts a suggested response for the agent to review or send.",
        setup:
          "In Zendesk Admin > Extensions > Webhooks, configure a trigger for ticket creation events. Ensure Pinecone vector DB has your knowledge base articles indexed. Set the confidence threshold to 85 for auto-responses.",
        inputFields: [],
      }),

      caseStampNode("2", "Stamp: Support copilot case", 200, "Support Copilot"),

      aiNodeV2(
        "3",
        "OpenAI: Classify ticket and suggest response with KB evidence",
        360,
        "You are a customer support copilot. Classify the ticket by category, urgency, and complexity. Using the knowledge base search results, draft a helpful, accurate response that directly addresses the customer's issue. Include specific article references as citations. Output ticket_category (billing/technical/account/feature/bug/other), urgency (low/medium/high/critical), complexity (simple/moderate/complex), suggested_response (the draft reply with KB citations), kb_articles_used (comma-separated article IDs), and estimated_resolution_time (in hours).",
        "New ticket: subject {{{1.subject}}}, description {{{1.description}}}, requester {{{1.requester.email}}}. Pinecone KB search results: {{{4.kb_matches}}}. Zendesk requester history: {{{4.requester_tickets}}} previous tickets, satisfaction {{{4.satisfaction_rating}}}. Draft a response using KB evidence.",
        [
          { field: "ticket_category", as: "ticket_category", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "complexity", as: "complexity", type: "string" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
          { field: "kb_articles_used", as: "kb_articles", type: "string" },
          {
            field: "estimated_resolution_time",
            as: "est_resolution_hrs",
            type: "number",
          },
        ],
      ),

      n(
        "4",
        "hitApi",
        "Pinecone + Zendesk: Search KB and fetch requester data",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_host}/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{{{1.description_embedding}}}" },
              { key: "topK", value: "5" },
              { key: "includeMetadata", value: "true" },
            ],
            variables: [
              { name: "matches", customName: "kb_matches", type: "string" },
              {
                name: "matches[0].metadata.article_title",
                customName: "top_article_title",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "top_match_score",
                type: "number",
              },
            ],
          },
        },
      ),

      confidenceGate("5", "Gate: AI response confidence >= 85?", 680, "85"),

      n(
        "6",
        "hitApi",
        "Zendesk: Post suggested response as internal note",
        840,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{{{1.id}}}",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"comment":{"body":"{{{3.suggested_response}}}","public":false,"author_id":"{zendesk_bot_id}"},"tags":["ai_copilot","{{{3.ticket_category}}}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "updated_ticket_id",
                type: "string",
              },
            ],
          },
        },
      ),

      approvalNode(
        "7",
        "Approval: Agent reviews AI suggestion",
        840,
        'AI Copilot suggestion for ticket #{{{1.id}}}: "{{{1.subject}}}". Category: {{{3.ticket_category}}} ({{{3.urgency}}}). Suggested response: "{{{3.suggested_response}}}". KB articles: {{{3.kb_articles}}}. Confidence: {{{3.ai_confidence}}}%. Approve to post as internal note for agent.',
        4,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Response added to ticket",
        1000,
        "GET",
        "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{{{1.id}}}/comments?sort_order=desc&per_page=1",
        [{ key: "Authorization", value: "Basic {zendesk_auth}" }],
        [
          { name: "comments[0].id", customName: "comment_id", type: "string" },
          {
            name: "comments[0].public",
            customName: "is_public",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Response added?",
        1160,
        "comment_id",
        "IS NOT EMPTY",
        "",
      ),

      sheetsNode(
        "10",
        "Sheets: Log support copilot action",
        1320,
        "SupportCopilot",
        '[["{{{2.case_id}}}","{{{1.id}}}","{{{1.subject}}}","{{{3.ticket_category}}}","{{{3.urgency}}}","{{{3.complexity}}}","{{{3.kb_articles}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Acts as an AI copilot for support agents. On new Zendesk tickets, searches Pinecone for relevant KB articles, classifies urgency and complexity, then drafts a suggested response with citations. High-confidence responses (85%+) auto-post as internal notes; uncertain suggestions route to agent approval. Verifies the comment was added and logs all copilot actions for response quality analytics.",
  },

  // ===================================================================
  // 75. Elite: Competitor Intel Digest
  // ===================================================================
  "Elite: Competitor Intel Digest": {
    nodes: [
      trigger("Schedule: Weekly competitor analysis", "schedule", {
        schedule:
          "Every Monday at 7:00 AM — compiles weekly competitor intelligence digest",
        howItStarts:
          "Runs weekly to aggregate competitor news, product updates, pricing changes, and market positioning shifts from web research APIs and news feeds, compiling a strategic intelligence digest for the leadership team.",
        setup:
          "Configure weekly schedule for Monday 7 AM. Set up web research API keys (Serper, NewsAPI, or similar). Define competitor list in workflow variables. Ensure email distribution list is configured.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Competitor intel case",
        200,
        "Competitor Intel Digest",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze competitor news and positioning changes",
        360,
        "You are a competitive intelligence analyst. Analyze the collected competitor news, blog posts, product announcements, and hiring patterns to identify strategic shifts, threats, and opportunities. For each major competitor, summarize recent moves and assess their impact on our market position. Output digest_summary (executive summary paragraph), competitor_moves (structured list of competitor:action pairs), threat_level (low/moderate/elevated/high), top_opportunity (our best response opportunity), market_trend (overall market direction), and recommended_actions (3 specific strategic recommendations).",
        "Competitor research data: {{{4.news_results}}}. Competitors monitored: {{{4.competitor_list}}}. Previous week's digest highlights: {{{4.prev_highlights}}}. Analyze trends, threats, and opportunities for this week's digest.",
        [
          { field: "digest_summary", as: "digest_summary", type: "string" },
          { field: "competitor_moves", as: "competitor_moves", type: "string" },
          { field: "threat_level", as: "threat_level", type: "string" },
          { field: "top_opportunity", as: "top_opportunity", type: "string" },
          { field: "market_trend", as: "market_trend", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),

      n("4", "hitApi", "Web Research: Fetch competitor news and updates", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "q",
              value:
                "{competitor_names} product launch OR funding OR partnership OR pricing",
            },
            { key: "num", value: "20" },
            { key: "tbs", value: "qdr:w" },
          ],
          variables: [
            { name: "organic", customName: "news_results", type: "string" },
            {
              name: "organic.length",
              customName: "articles_found",
              type: "number",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI analysis confidence?", 680),

      emailNode(
        "6",
        "Email: Send weekly competitor digest",
        840,
        "{strategy_team_dl}",
        "Weekly Competitor Intel Digest — Threat Level: {{{3.threat_level}}}",
        "COMPETITOR INTELLIGENCE DIGEST\nWeek of {{{2.started_at}}}\n\nEXECUTIVE SUMMARY\n{{{3.digest_summary}}}\n\nCOMPETITOR MOVES\n{{{3.competitor_moves}}}\n\nMARKET TREND: {{{3.market_trend}}}\n\nTOP OPPORTUNITY\n{{{3.top_opportunity}}}\n\nRECOMMENDED ACTIONS\n{{{3.recommended_actions}}}\n\nCase ID: {{{2.case_id}}}",
      ),

      approvalNode(
        "7",
        "Approval: Strategy team reviews digest",
        840,
        "Weekly competitor digest ready. Threat level: {{{3.threat_level}}}. Market trend: {{{3.market_trend}}}. Top opportunity: {{{3.top_opportunity}}}. {{{4.articles_found}}} articles analyzed. Confidence: {{{3.ai_confidence}}}%. Approve to distribute to leadership.",
        24,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Digest email sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=competitor-digest&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "digests_sent", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "digest_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Digest sent?",
        1160,
        "digest_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log competitor digest",
        1320,
        "CompetitorDigests",
        '[["{{{2.case_id}}}","{{{3.threat_level}}}","{{{3.market_trend}}}","{{{4.articles_found}}}","{{{3.top_opportunity}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Runs weekly to aggregate competitor news via web research APIs, then uses AI to analyze positioning shifts, identify threats and opportunities, and compile a structured digest with strategic recommendations. High-confidence digests auto-send to leadership; uncertain analyses route to strategy team review. Verifies delivery and logs all digests for competitive intelligence tracking.",
  },

  // ===================================================================
  // 76. Elite: Data Quality Auditor
  // ===================================================================
  "Elite: Data Quality Auditor": {
    nodes: [
      trigger("Schedule: Daily CRM data quality audit", "schedule", {
        schedule:
          "Daily at 6:00 AM — audits CRM data quality across contact records",
        howItStarts:
          "Runs daily to sample HubSpot contact records and audit them for data quality issues including missing fields, inconsistent formatting, duplicate indicators, stale data, and invalid email formats.",
        setup:
          "Configure daily 6 AM schedule. Ensure HubSpot API key has read access to contacts. Set up Slack webhook for the #revops-alerts channel. Define quality thresholds in workflow variables.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Data quality audit case",
        200,
        "Data Quality Auditor",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze CRM data quality issues",
        360,
        "You are a CRM data quality auditor. Analyze the sample of contact records for data quality issues. Check for: missing required fields (email, company, lifecycle stage), invalid email formats, phone number formatting inconsistencies, duplicate indicators (same name+company), stale records (no activity in 180+ days), and incomplete lead scoring. Output quality_score (0-100), total_issues_found, critical_issues (count of blocking problems), issue_breakdown (category:count pairs), top_fix_recommendations (3 most impactful fixes), and estimated_fix_effort (hours).",
        "HubSpot sample records: {{{4.sample_records}}}. Total contacts in CRM: {{{4.total_contacts}}}. Previous audit score: {{{4.prev_score}}}. Audit these records for data quality issues and provide a comprehensive quality report.",
        [
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "total_issues_found", as: "total_issues", type: "number" },
          { field: "critical_issues", as: "critical_issues", type: "number" },
          { field: "issue_breakdown", as: "issue_breakdown", type: "string" },
          {
            field: "top_fix_recommendations",
            as: "fix_recommendations",
            type: "string",
          },
          {
            field: "estimated_fix_effort",
            as: "fix_effort_hours",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "HubSpot: Fetch sample contact records", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "limit", value: "100" },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","lifecyclestage","phone","hs_lead_status","hs_last_activity_date"]',
            },
            {
              key: "sorts",
              value:
                '[{"propertyName":"lastmodifieddate","direction":"DESCENDING"}]',
            },
          ],
          variables: [
            { name: "results", customName: "sample_records", type: "string" },
            { name: "total", customName: "total_contacts", type: "number" },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI audit confidence?", 680),

      slackNode(
        "6",
        "Slack: Post data quality report",
        840,
        "CRM Data Quality Audit Report\n\nQuality Score: {{{3.quality_score}}}/100\nTotal Issues: {{{3.total_issues}}} ({{{3.critical_issues}}} critical)\nIssue Breakdown: {{{3.issue_breakdown}}}\n\nTop Fix Recommendations:\n{{{3.fix_recommendations}}}\n\nEstimated Fix Effort: {{{3.fix_effort_hours}}} hours\n\nCase: {{{2.case_id}}}",
      ),

      approvalNode(
        "7",
        "Approval: RevOps reviews quality report",
        840,
        "CRM data quality audit complete. Score: {{{3.quality_score}}}/100. {{{3.total_issues}}} issues ({{{3.critical_issues}}} critical). Top fixes: {{{3.fix_recommendations}}}. Fix effort: {{{3.fix_effort_hours}}} hrs. Confidence: {{{3.ai_confidence}}}%. Approve to post report to #revops-alerts.",
        24,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Report sent to Slack",
        1000,
        "GET",
        "https://slack.com/api/conversations.history?channel={revops_channel}&limit=1",
        [{ key: "Authorization", value: "Bearer {slack_bot_token}" }],
        [
          { name: "messages[0].ts", customName: "report_ts", type: "string" },
          { name: "ok", customName: "report_sent", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Report posted?",
        1160,
        "report_sent",
        "EQUALS",
        "true",
      ),

      sheetsNode(
        "10",
        "Sheets: Log data quality audit",
        1320,
        "DataQualityAudits",
        '[["{{{2.case_id}}}","{{{3.quality_score}}}","{{{3.total_issues}}}","{{{3.critical_issues}}}","{{{3.fix_effort_hours}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Runs daily to sample HubSpot contacts and use AI to audit data quality across multiple dimensions (missing fields, invalid formats, duplicates, stale records). Generates a quality score, categorizes issues, and recommends prioritized fixes. High-confidence reports auto-post to Slack; uncertain audits route to RevOps review. Verifies posting and logs all audits for quality trend tracking.",
  },

  // ===================================================================
  // 77. Elite: AI Document Summarizer
  // ===================================================================
  "Elite: AI Document Summarizer": {
    nodes: [
      trigger("Google Drive: Document uploaded or shared", "webhook_app", {
        source: "google_drive",
        event: "files.created",
        howItStarts:
          "Fires when a new document is uploaded to a monitored Google Drive folder. Supports PDFs, Google Docs, and Word files. Extracts the content and generates a structured summary with key points for quick review.",
        setup:
          "Configure Google Drive push notifications for a shared folder. Ensure the service account has read access. Set up a Notion integration for creating summary pages. Support file types: PDF, DOCX, Google Docs.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Document summary case",
        200,
        "AI Document Summarizer",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Summarize document and extract key points",
        360,
        "You are a professional document analyst. Create a comprehensive yet concise summary of the document. Extract key points, decisions, action items, important dates/deadlines, and notable quotes. Structure the summary for quick scanning. Output executive_summary (2-3 sentence overview), key_points (numbered list of 5-10 main points), action_items (extracted tasks with owners if mentioned), important_dates (any deadlines or milestones), document_type (report/proposal/contract/memo/research/other), and reading_time_saved_minutes (estimated time saved by reading summary vs full doc).",
        "Document content from Google Drive: {{{4.doc_content}}}. File metadata: name {{{4.file_name}}}, type {{{4.mime_type}}}, size {{{4.file_size}}}, created_by {{{4.owner_email}}}. Create a structured summary with key point extraction.",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "important_dates", as: "important_dates", type: "string" },
          { field: "document_type", as: "doc_type", type: "string" },
          {
            field: "reading_time_saved_minutes",
            as: "time_saved",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "Google Drive: Fetch document content", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/drive/v3/files/{{{1.file_id}}}?alt=media",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "body", customName: "doc_content", type: "string" },
            { name: "name", customName: "file_name", type: "string" },
            { name: "mimeType", customName: "mime_type", type: "string" },
            { name: "size", customName: "file_size", type: "string" },
            {
              name: "owners[0].emailAddress",
              customName: "owner_email",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI summary confidence?", 680),

      n("6", "hitApi", "Notion: Create summary page", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{notion_summaries_db}"}' },
            {
              key: "properties",
              value:
                '{"Name":{"title":[{"text":{"content":"Summary: {{{4.file_name}}}"}}]},"Type":{"select":{"name":"{{{3.doc_type}}}"}},"Source":{"url":"https://drive.google.com/file/d/{{{1.file_id}}}"}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
            { name: "url", customName: "notion_page_url", type: "string" },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Reviewer checks summary quality",
        840,
        'AI summarized "{{{4.file_name}}}" ({{{3.doc_type}}}). Summary: {{{3.exec_summary}}}. Key points extracted: {{{3.key_points}}}. Action items: {{{3.action_items}}}. Time saved: {{{3.time_saved}}} min. Confidence: {{{3.ai_confidence}}}%. Approve to publish Notion page.',
        24,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Notion page created",
        1000,
        "GET",
        "https://api.notion.com/v1/pages/{{{6.notion_page_id}}}",
        [
          { key: "Authorization", value: "Bearer {notion_token}" },
          { key: "Notion-Version", value: "2022-06-28" },
        ],
        [
          { name: "id", customName: "verified_page_id", type: "string" },
          { name: "archived", customName: "page_archived", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Summary page created?",
        1160,
        "verified_page_id",
        "IS NOT EMPTY",
        "",
      ),

      sheetsNode(
        "10",
        "Sheets: Log document summary",
        1320,
        "DocSummaries",
        '[["{{{2.case_id}}}","{{{4.file_name}}}","{{{3.doc_type}}}","{{{3.time_saved}}}","{{{6.notion_page_url}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Monitors Google Drive for new documents, fetches content, then uses AI to generate structured summaries with key points, action items, and important dates. High-confidence summaries auto-publish to Notion; lower-quality ones route to reviewer approval. Verifies Notion page creation and logs all summaries for knowledge management metrics including time saved.",
  },

  // ===================================================================
  // 78. Elite: Slack FAQ Bot with Escalation
  // ===================================================================
  "Elite: Slack FAQ Bot with Escalation": {
    nodes: [
      trigger("Slack: Question posted in help channel", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when someone posts a question in a Slack help or FAQ channel. The bot searches the knowledge base for relevant answers, provides a response with source citations, or escalates to a human via Jira ticket if confidence is low.",
        setup:
          "Configure Slack bot with event subscriptions for message.channels in designated help channels (#help, #support-internal, #faq). Index your knowledge base in Pinecone. Set up Jira project for escalated questions.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: FAQ bot case",
        200,
        "Slack FAQ Bot with Escalation",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Answer question from KB with citations",
        360,
        "You are an internal FAQ assistant. Using the knowledge base search results, provide an accurate, helpful answer to the question. Always cite your sources with specific article titles and links. If the KB does not contain a clear answer, say so honestly rather than guessing. Output answer_text (the response with inline citations), sources_cited (comma-separated article titles), answer_quality (definitive/partial/no_match), is_escalation_needed (true/false), and suggested_kb_gap (topic that should be added to KB if answer was weak).",
        "Slack question from {{{1.user}}}: {{{1.text}}}. Pinecone KB matches: {{{4.kb_results}}}. Top match score: {{{4.top_score}}}. Answer the question using KB evidence with citations.",
        [
          { field: "answer_text", as: "answer_text", type: "string" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
          { field: "answer_quality", as: "answer_quality", type: "string" },
          {
            field: "is_escalation_needed",
            as: "needs_escalation",
            type: "string",
          },
          { field: "suggested_kb_gap", as: "kb_gap", type: "string" },
        ],
      ),

      n("4", "hitApi", "Pinecone: Search knowledge base", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_host}/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{{{1.text_embedding}}}" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
          ],
          variables: [
            { name: "matches", customName: "kb_results", type: "string" },
            {
              name: "matches[0].score",
              customName: "top_score",
              type: "number",
            },
            {
              name: "matches[0].metadata.title",
              customName: "top_article_title",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI answer confidence?", 680),

      slackNode(
        "6",
        "Slack: Reply with sourced answer",
        840,
        "Hey <@{{{1.user}}}>! Here's what I found:\n\n{{{3.answer_text}}}\n\nSources: {{{3.sources_cited}}}\n\n_Answered by AI FAQ Bot (Case: {{{2.case_id}}})_",
      ),

      n(
        "7",
        "hitApi",
        "Jira: Create escalation ticket for human",
        840,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"{jira_support_project}"},"summary":"Slack FAQ Escalation: {{{1.text}}}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Question from {{{1.user}}}: {{{1.text}}}. AI answer quality: {{{3.answer_quality}}}. KB gap: {{{3.kb_gap}}}. Case: {{{2.case_id}}}"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"Medium"}}',
              },
            ],
            variables: [
              { name: "id", customName: "jira_issue_id", type: "string" },
              { name: "key", customName: "jira_issue_key", type: "string" },
            ],
          },
        },
        480,
      ),

      verifyNode(
        "8",
        "Verify: Reply sent or ticket created",
        1000,
        "GET",
        "https://slack.com/api/conversations.history?channel={{{1.channel}}}&limit=1",
        [{ key: "Authorization", value: "Bearer {slack_bot_token}" }],
        [
          { name: "messages[0].ts", customName: "reply_ts", type: "string" },
          { name: "ok", customName: "reply_sent", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Reply or ticket created?",
        1160,
        "reply_sent",
        "EQUALS",
        "true",
      ),

      sheetsNode(
        "10",
        "Sheets: Log FAQ interaction",
        1320,
        "FAQBotLog",
        '[["{{{2.case_id}}}","{{{1.user}}}","{{{1.text}}}","{{{3.answer_quality}}}","{{{3.sources_cited}}}","{{{3.needs_escalation}}}","{{{3.kb_gap}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Answers Slack questions by searching a Pinecone knowledge base and using AI to compose cited responses. High-confidence answers auto-reply in Slack with source links; low-confidence questions create Jira escalation tickets for human follow-up and identify KB gaps. Verifies response delivery and logs all interactions for FAQ effectiveness and KB improvement tracking.",
  },

  // ===================================================================
  // 79. Elite: Meeting Action Items Tracker
  // ===================================================================
  "Elite: Meeting Action Items Tracker": {
    nodes: [
      trigger("Zoom: Recording and transcript ready", "webhook_app", {
        source: "zoom",
        event: "recording.completed",
        howItStarts:
          "Fires when a Zoom meeting recording and auto-generated transcript are ready. Extracts action items, owners, and deadlines from the transcript and creates tasks in Asana for tracking.",
        setup:
          "In Zoom Marketplace, configure webhook for recording.completed events. Enable cloud recording and auto-transcription. Ensure Asana API token has project write access. Map Zoom users to Asana user IDs.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Action items case",
        200,
        "Meeting Action Items Tracker",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Extract action items, owners, and deadlines",
        360,
        "You are a meeting productivity assistant. Parse the meeting transcript to extract every action item mentioned. For each action item, identify the owner (who committed to doing it), the deadline (if mentioned), and the priority (high/medium/low based on context and urgency). Also capture key decisions made and parking lot items. Output action_items_json (array of {task, owner, deadline, priority}), total_action_items (count), key_decisions (comma-separated), parking_lot_items (topics deferred), and meeting_effectiveness_score (1-10 based on clarity of outcomes).",
        "Zoom meeting transcript: {{{4.transcript}}}. Meeting: {{{4.meeting_topic}}}, duration {{{4.duration}}} minutes, participants: {{{4.participants}}}. Extract all action items with owners and deadlines.",
        [
          {
            field: "action_items_json",
            as: "action_items_json",
            type: "string",
          },
          { field: "total_action_items", as: "total_actions", type: "number" },
          { field: "key_decisions", as: "key_decisions", type: "string" },
          { field: "parking_lot_items", as: "parking_lot", type: "string" },
          {
            field: "meeting_effectiveness_score",
            as: "effectiveness_score",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "Zoom: Fetch meeting transcript", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/meetings/{{{1.payload.object.id}}}/recordings",
          headers: [{ key: "Authorization", value: "Bearer {zoom_token}" }],
          body: [],
          variables: [
            {
              name: "recording_files[0].download_url",
              customName: "transcript_url",
              type: "string",
            },
            { name: "topic", customName: "meeting_topic", type: "string" },
            { name: "duration", customName: "duration", type: "number" },
            {
              name: "participants_count",
              customName: "participants",
              type: "string",
            },
            { name: "vtt", customName: "transcript", type: "string" },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI extraction confidence?", 680),

      n("6", "hitApi", "Asana: Create tasks from action items", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[Meeting AI] {{{4.meeting_topic}}} — Action Items","notes":"Meeting: {{{4.meeting_topic}}}\\nAction Items: {{{3.action_items_json}}}\\nKey Decisions: {{{3.key_decisions}}}\\nParking Lot: {{{3.parking_lot}}}\\n\\nCase: {{{2.case_id}}}","projects":["{asana_project_id}"],"due_on":"{next_friday}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Meeting organizer reviews action items",
        840,
        'Extracted {{{3.total_actions}}} action items from "{{{4.meeting_topic}}}" ({{{4.duration}}} min). Decisions: {{{3.key_decisions}}}. Effectiveness: {{{3.effectiveness_score}}}/10. Parking lot: {{{3.parking_lot}}}. Confidence: {{{3.ai_confidence}}}%. Approve to create Asana tasks.',
        12,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Asana tasks created",
        1000,
        "GET",
        "https://app.asana.com/api/1.0/tasks/{{{6.asana_task_id}}}",
        [{ key: "Authorization", value: "Bearer {asana_token}" }],
        [
          { name: "data.gid", customName: "verified_task_id", type: "string" },
          {
            name: "data.completed",
            customName: "task_completed",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Tasks created?",
        1160,
        "verified_task_id",
        "IS NOT EMPTY",
        "",
      ),

      sheetsNode(
        "10",
        "Sheets: Log meeting action items",
        1320,
        "MeetingActions",
        '[["{{{2.case_id}}}","{{{4.meeting_topic}}}","{{{4.duration}}}","{{{3.total_actions}}}","{{{3.effectiveness_score}}}","{{{6.asana_task_url}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes completed Zoom recordings by fetching transcripts and using AI to extract action items with owners, deadlines, and priorities. Also captures key decisions and parking lot items. High-confidence extractions auto-create Asana tasks; uncertain ones route to the meeting organizer for review. Verifies task creation and logs all meetings for productivity analytics.",
  },

  // ===================================================================
  // 80. Elite: Cross-Team Launch Checklist
  // ===================================================================
  "Elite: Cross-Team Launch Checklist": {
    nodes: [
      trigger("Asana: Launch project created", "webhook_app", {
        source: "asana",
        event: "project.created",
        howItStarts:
          "Fires when a new launch project is created in Asana (tagged with 'launch' or in the Launches portfolio). Generates a comprehensive, team-specific checklist with tasks for engineering, marketing, sales, support, and legal.",
        setup:
          "Configure Asana webhook for project creation in the Launches portfolio. Ensure the service account has project write access. Define team-specific checklist templates in workflow variables. Map team leads to Asana user GIDs.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Launch checklist case",
        200,
        "Cross-Team Launch Checklist",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Generate team-specific launch checklist",
        360,
        "You are a product launch operations manager. Based on the launch project details, generate a comprehensive checklist broken down by team. Each team's checklist should include specific, actionable tasks with realistic deadlines relative to the launch date. Output engineering_tasks (5-8 tasks with deadlines), marketing_tasks (5-8 tasks), sales_tasks (3-5 tasks), support_tasks (3-5 tasks), legal_compliance_tasks (2-3 tasks), total_tasks (total count), estimated_prep_weeks (weeks needed before launch), and launch_readiness_criteria (3 must-meet conditions).",
        "Launch project: name {{{4.project_name}}}, description {{{4.project_notes}}}, target_launch {{{4.due_date}}}, created_by {{{4.owner}}}. Teams involved: engineering, marketing, sales, support, legal. Generate a complete cross-team launch checklist with deadlines relative to the launch date.",
        [
          {
            field: "engineering_tasks",
            as: "engineering_tasks",
            type: "string",
          },
          { field: "marketing_tasks", as: "marketing_tasks", type: "string" },
          { field: "sales_tasks", as: "sales_tasks", type: "string" },
          { field: "support_tasks", as: "support_tasks", type: "string" },
          {
            field: "legal_compliance_tasks",
            as: "legal_tasks",
            type: "string",
          },
          { field: "total_tasks", as: "total_tasks", type: "number" },
          { field: "estimated_prep_weeks", as: "prep_weeks", type: "number" },
          {
            field: "launch_readiness_criteria",
            as: "readiness_criteria",
            type: "string",
          },
        ],
      ),

      n("4", "hitApi", "Asana: Fetch launch project details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/projects/{{{1.resource.gid}}}",
          headers: [{ key: "Authorization", value: "Bearer {asana_token}" }],
          body: [],
          variables: [
            { name: "data.name", customName: "project_name", type: "string" },
            { name: "data.notes", customName: "project_notes", type: "string" },
            { name: "data.due_date", customName: "due_date", type: "string" },
            { name: "data.owner.name", customName: "owner", type: "string" },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI checklist confidence?", 680),

      n("6", "hitApi", "Asana: Create team sub-tasks in project", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Launch Checklist: {{{4.project_name}}} ({{{3.total_tasks}}} tasks)","notes":"Generated by OmniEngage Launch Checklist Agent\\n\\nENGINEERING:\\n{{{3.engineering_tasks}}}\\n\\nMARKETING:\\n{{{3.marketing_tasks}}}\\n\\nSALES:\\n{{{3.sales_tasks}}}\\n\\nSUPPORT:\\n{{{3.support_tasks}}}\\n\\nLEGAL:\\n{{{3.legal_tasks}}}\\n\\nReadiness Criteria:\\n{{{3.readiness_criteria}}}\\n\\nPrep Time: {{{3.prep_weeks}}} weeks\\nCase: {{{2.case_id}}}","projects":["{{{1.resource.gid}}}"],"due_on":"{{{4.due_date}}}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "checklist_task_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "checklist_url",
              type: "string",
            },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Project lead reviews checklist",
        840,
        'Launch checklist for "{{{4.project_name}}}" generated: {{{3.total_tasks}}} tasks across 5 teams. Prep time: {{{3.prep_weeks}}} weeks. Readiness criteria: {{{3.readiness_criteria}}}. Confidence: {{{3.ai_confidence}}}%. Approve to create Asana tasks in the project.',
        24,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Tasks created in Asana",
        1000,
        "GET",
        "https://app.asana.com/api/1.0/tasks/{{{6.checklist_task_id}}}",
        [{ key: "Authorization", value: "Bearer {asana_token}" }],
        [
          {
            name: "data.gid",
            customName: "verified_checklist_id",
            type: "string",
          },
          { name: "data.name", customName: "checklist_name", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Checklist tasks created?",
        1160,
        "verified_checklist_id",
        "IS NOT EMPTY",
        "",
      ),

      sheetsNode(
        "10",
        "Sheets: Log launch checklist",
        1320,
        "LaunchChecklists",
        '[["{{{2.case_id}}}","{{{4.project_name}}}","{{{3.total_tasks}}}","{{{3.prep_weeks}}}","{{{4.due_date}}}","{{{6.checklist_url}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Automates launch planning when a new Asana project is created. Fetches project details, then uses AI to generate a comprehensive cross-team checklist with specific tasks for engineering, marketing, sales, support, and legal. High-confidence checklists auto-create tasks in Asana; complex launches route to project lead approval. Verifies task creation and logs all launches for operational planning metrics.",
  },
};
